<template>
  <div class="fill-height align-content-center">
    <v-simple-table class="pb-0" dense>
      <template #default>
        <thead class="table-heading">
          <tr>
            <th rowspan="2" :class="tableHeaderCellClass">
              <v-tooltip top>
                <template #activator="{ on, attrs }">
                  <span v-bind="attrs" v-on="on"
                    >Promedio diario de <br />
                    pedidos de los ultimos 6 meses
                    {{
                      `(${orders.data?.orders.businessDaysLast6Months ?? 0})`
                    }}
                  </span>
                </template>
                <b>
                  {{
                    `${
                      orders.data?.orders.businessDaysLast6Months ?? 0
                    } Días hábiles`
                  }}
                </b>
              </v-tooltip>
            </th>
            <th rowspan="2" :class="tableHeaderCellClass">
              <span>
                Monto de pedidos <br />
                día anterior
              </span>
            </th>
            <th colspan="3" :class="tableColumnHeaderClass">
              <span>Acumulado Ctes Nuevos</span>
            </th>
            <th colspan="2" :class="tableColumnHeaderClass">
              <span>Crecimiento Facturación neta</span>
            </th>
            <th rowspan="2" :class="tableHeaderCellClass">
              <span>
                Avance facturación <br />
                VS <br />
                Presupuesto, acumulado
              </span>
            </th>
          </tr>
          <tr>
            <th :class="tableSubHeaderCellClass"><span>#Clientes</span></th>
            <th :class="tableSubHeaderTextCellClass">
              <span>
                Facturación <br />
                neta
              </span>
            </th>
            <th :class="tableSubHeaderTextCellClass">
              <span>
                Ticket <br />
                promedio
              </span>
            </th>
            <th :class="tableComparisonHeaderCellClass">
              <span>
                Acum <br />
                {{ currentYearComparisonLabel }}
              </span>
            </th>
            <th :class="tableComparisonHeaderCellClassAlt">
              <span>
                Acum <br />
                {{ previousYearComparisonLabel }}
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr class="text-body-2">
            <template v-if="orders.loading">
              <td colspan="8" class="text-center">
                <v-skeleton-loader class="mx-auto" type="table-row" />
              </td>
            </template>
            <template v-else>
              <td>
                {{
                  formatCurrency(orders.data?.orders.avgDailyAmountLast6Months)
                }}
              </td>
              <td>
                <b>{{ formatCurrency(orders.data?.orders.lastDayAmount) }}</b>
              </td>
              <td>
                {{
                  formatNumber(
                    orders.data?.customers.monthlyCloseCount ?? 0,
                    '',
                    0
                  )
                }}
              </td>
              <td>
                {{ formatCurrency(orders.data?.customers.monthlyCloseNet) }}
              </td>
              <td>
                {{
                  formatCurrency(orders.data?.customers.avgTicketMonthlyClose)
                }}
              </td>
              <td>
                <b
                  class="text-decoration-underline cursor-pointer"
                  @click="showCumulativeGrowth(1)"
                >
                  {{
                    formatFloat(orders.data?.growth.invoiceAccumulated ?? 0, 2)
                  }}%
                </b>
              </td>
              <td>
                <b
                  class="text-decoration-underline cursor-pointer"
                  @click="showCumulativeGrowth(2)"
                >
                  {{
                    formatFloat(
                      orders.data?.growth.invoiceAccumulatedAlt ?? 0,
                      2
                    )
                  }}%
                </b>
              </td>
              <td>
                <b
                  class="text-decoration-underline cursor-pointer"
                  @click="billingVsEst()"
                >
                  {{
                    formatFloat(
                      orders.data?.growth.invoiceAccumulatedVsBudgetPercent ??
                        0,
                      2
                    )
                  }}%
                </b>
              </td>
            </template>
          </tr>
          <tr></tr>
        </tbody>
      </template>
    </v-simple-table>
    <template v-if="orders.loading">
      <v-skeleton-loader class="mx-auto" type="table-row" />
    </template>
    <v-simple-table v-else class="mt-0 my-table pb-0" dense>
      <template #default>
        <thead class="table-heading">
          <tr>
            <th
              class="text-center text-dark custom-border-right font-weight-bold"
            >
              <span v-if="isSeller"> % del Cte. Más grande / </span>
              % de pagos en línea
            </th>
            <th
              v-for="(header, index) in headers"
              :key="index"
              class="text-center text-dark custom-border-right font-weight-bold"
            >
              <span class="pre-line">{{ header.text }}</span>
            </th>
          </tr>
        </thead>
        <tbody class="custom-table-bordered">
          <tr class="text-body-2">
            <td>
              <span v-if="isSeller">
                {{
                  formatFloat(orders.data?.orders.majorCustomerPercent ?? 0, 2)
                }}% /
              </span>
              <a
                class="text-decoration-underline text-black"
                @click="loadIelPayment"
              >
                {{
                  formatFloat(orders.data?.orders.linePaymentPercent ?? 0, 2)
                }}%
              </a>
            </td>
            <td
              v-for="quarter in yearlyQuarterValues"
              :key="quarter.key"
              :class="quarter.className"
            >
              {{ formatCurrency(quarter.value) }}
            </td>
          </tr>
          <tr></tr>
        </tbody>
      </template>
    </v-simple-table>

    <cumulative-growth
      v-model="cumulativeGrowth.modal"
      current-key-value="detail.current"
      old-key-value="detail.last"
      month-key-value="SPMON"
      amount-key-value="FACPER"
      total-current-key-value="sumBillingActual"
      total-old-key-value="sumBillingCompared"
      total-percent-key-value="cumulativeGrowth"
      :loading="cumulativeGrowth.loading"
      :data="cumulativeGrowth.data"
    />

    <billing-vs-estimate
      v-model="billVsEst.modal"
      :loading="billVsEst.loading"
      :data="billVsEst.data"
    />

    <customers-i-e-l-payment
      v-model="ielPayment.modal"
      :loading="ielPayment.loading"
      :data="ielPayment.data"
    />
  </div>
</template>

<script>
  import { Roles } from '@/core/constants/roleConstants';
  import activeUsernameMixin from '@/mixins/activeUsernameMixin';
  import MixinService from '@/mixins/service';
  import numbersFormats from '@/mixins/numbersFormats';
  import {
    getOrders,
    getIelPaymentResume,
    getCumulativeGrowth,
    getBillingVsEstimate,
  } from '@/repositories/Daily';
  import CumulativeGrowth from '@/components/modals/CumulativeGrowth.vue';
  import BillingVsEstimate from '@/views/daily/modals/BillingVsEstimate.vue';
  import CustomersIELPayment from '@/views/daily/modals/CustomersIELPayment.vue';

  export default {
    name: 'OrdersTable',
    components: {
      BillingVsEstimate,
      CumulativeGrowth,
      CustomersIELPayment,
    },
    mixins: [MixinService, numbersFormats, activeUsernameMixin],
    props: {
      userId: {
        default: '',
        type: String,
      },
      chnl: {
        required: true,
        type: String,
      },
    },
    data: () => ({
      username: '',
      orders: {
        loading: true,
        data: null,
      },
      cumulativeGrowth: {
        modal: false,
        loading: false,
        data: null,
      },
      billVsEst: {
        modal: false,
        loading: false,
        data: null,
      },
      months: [],
      headers: [],
      ielPayment: {
        modal: false,
        data: {
          PORPAGLIN: 0,
          PAGLINTRI: 0,
          PORPAGLINTRI: 0,
        },
        loading: false,
      },
      tableHeaderCellClass:
        'text-center text-dark custom-border-right text-nowrap',
      tableColumnHeaderClass: 'text-center text-dark custom-border-right',
      tableSubHeaderCellClass: 'text-black',
      tableSubHeaderTextCellClass: 'text-center text-black',
      tableComparisonHeaderCellClass: 'text-center text-black text-nowrap',
      tableComparisonHeaderCellClassAlt: 'text-center text-nowrap text-black',
    }),
    computed: {
      isSeller() {
        return this.activeUsername?.role === Roles.SELLER;
      },
      currentYearComparisonLabel() {
        if (!this.months[0]) return '';
        return `${this.months[0].year} - ${this.months[0].year - 1}`;
      },
      previousYearComparisonLabel() {
        if (!this.months[0]) return '';
        return `${this.months[0].year} - ${this.months[0].year - 2}`;
      },
      yearlyQuarterValues() {
        const yearlyNet = this.orders.data?.billing?.yearlyNet || {};

        return Array.from({ length: 5 }, (_, index) => {
          const quarterKey = `q${6 - (index + 1)}`;
          const value = yearlyNet[quarterKey] ?? 0;

          return {
            key: quarterKey,
            value,
            className: this.billingTextClass(value),
          };
        });
      },
    },
    created() {
      this.months = this.calcularUltimosMeses(13);
      const periods = this.getQuartersList(5).reverse();
      this.headers.push(
        ...periods.map((period) => {
          return {
            text: `Trim \n ${period.quarter}/${period.year}`,
            value: period.value,
            align: 'center',
          };
        })
      );
    },
    mounted() {
      this.username = this.userId ? this.userId : this.activeUsername?.username;
      this.loadOrders();
    },

    methods: {
      formatCurrency(value, digits = 0) {
        return this.formatNumber(value ?? 0, '$', digits);
      },
      async loadOrders() {
        try {
          this.orders.loading = true;
          this.orders.data = await getOrders(this.username, this.chnl);
        } finally {
          this.orders.loading = false;
        }
      },

      billingTextClass(value) {
        switch (value) {
          case this.orders.data?.billing.maxQuarter:
            return 'text-success font-weight-bold';
          case this.orders.data?.billing.minQuarter:
            return 'text-danger font-weight-bold';
          default:
            return '';
        }
      },
      async loadIelPayment() {
        try {
          this.ielPayment.loading = true;
          this.ielPayment.modal = true;
          this.ielPayment.data = await getIelPaymentResume(
            this.username,
            this.chnl
          );
        } catch (error) {
          this.ielPayment.modal = false;
          this.$swal(
            'Ups',
            'No se ha podido obtener la información de pagos en línea'
          );
        } finally {
          this.ielPayment.loading = false;
        }
      },

      async showCumulativeGrowth(period) {
        this.cumulativeGrowth.modal = true;
        this.cumulativeGrowth.loading = true;
        try {
          this.cumulativeGrowth.data = await getCumulativeGrowth(
            this.username,
            this.chnl,
            period
          );
          this.cumulativeGrowth.loading = false;
        } catch (error) {
          this.cumulativeGrowth.modal = false;
          this.$toast.error(
            'No se ha podido obtener la información del crecimiento'
          );
        }
      },
      async billingVsEst() {
        this.billVsEst.loading = true;
        this.billVsEst.modal = true;
        try {
          this.billVsEst.data = await getBillingVsEstimate(
            this.username,
            this.chnl
          );
        } catch (error) {
          this.billVsEst.modal = false;
          this.$toast.error('No se ha podido obtener la información');
        } finally {
          this.billVsEst.loading = false;
        }
      },
    },
  };
</script>
